import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowsLeftRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { formatItalianDate } from "@/lib/dateFormat";

const FIELDS = [
  { key: "peso_kg", label: "Peso", unit: "kg" },
  { key: "bodyfat_pct", label: "Body fat", unit: "%", scale: 100 },
  { key: "massa_magra_kg", label: "Massa magra", unit: "kg", higherIsBetter: true },
  { key: "avambraccio", label: "Avambraccio", unit: "cm" },
  { key: "braccio", label: "Braccio", unit: "cm" },
  { key: "torace", label: "Torace", unit: "cm", gender: "M" },
  { key: "glutei", label: "Glutei", unit: "cm", gender: "F" },
  { key: "giro_vita", label: "Giro vita", unit: "cm" },
  { key: "periombelicale", label: "Periombelicale", unit: "cm" },
  { key: "giro_coscia", label: "Giro coscia", unit: "cm" },
  { key: "vasto_mediale", label: "Vasto mediale", unit: "cm" },
  { key: "polpaccio", label: "Polpaccio", unit: "cm" },
];

const val = (m, f) => (m && m[f.key] ? m[f.key] * (f.scale || 1) : null);
const fmt = (v) => (v == null ? "—" : v.toLocaleString("it-IT", { maximumFractionDigits: 1, minimumFractionDigits: 1 }));

function DateStepper({ side, idx, total, date, onStep }) {
  const btn = "w-8 h-8 rounded-full border border-[color:var(--border)] disabled:opacity-30 hover:border-[color:var(--text-primary)] flex items-center justify-center";
  return (
    <div className="flex flex-col items-center">
      <div className="text-[10px] text-[color:var(--text-secondary)] uppercase tracking-widest mb-1">
        {side === "left" ? "Precedente" : "Successivo"}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`mcompare-${side}-prev`} onClick={() => onStep(-1)} disabled={idx === 0} className={btn}>
          <CaretLeft size={13} weight="bold" />
        </button>
        <div className="text-center min-w-[88px]">
          <div className="font-serif text-base tabular-nums" data-testid={`mcompare-${side}-date`}>{formatItalianDate(date)}</div>
          <div className="text-[10px] text-[color:var(--text-secondary)] tabular-nums">{idx + 1} / {total}</div>
        </div>
        <button type="button" data-testid={`mcompare-${side}-next`} onClick={() => onStep(1)} disabled={idx >= total - 1} className={btn}>
          <CaretRight size={13} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function DeltaCell({ a, b, field }) {
  if (a == null || b == null) return <span className="text-[color:var(--text-secondary)]">—</span>;
  const d = b - a;
  if (Math.abs(d) < 0.05) return <span className="text-[color:var(--text-secondary)] tabular-nums">0,0</span>;
  const good = field.higherIsBetter ? d > 0 : d < 0;
  const color = good ? "#3F7A4E" : "#B94A4A";
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium tabular-nums" style={{ color, background: `${color}1A` }}>
      {d > 0 ? "+" : "−"}{fmt(Math.abs(d))} {field.unit}
    </span>
  );
}

export default function MeasurementsCompare({ measurements, gender }) {
  const list = useMemo(
    () => [...measurements].sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [measurements]
  );
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState({ left: 0, right: 0 });
  const fields = FIELDS.filter((f) => !f.gender || f.gender === (gender || "M"));

  const openCompare = () => {
    setIdx({ left: 0, right: Math.max(list.length - 1, 0) });
    setOpen(true);
  };
  const step = (side, delta) =>
    setIdx((s) => ({ ...s, [side]: Math.min(Math.max(s[side] + delta, 0), list.length - 1) }));

  const L = list[idx.left];
  const R = list[idx.right];

  return (
    <>
      <Button data-testid="measurements-compare-btn" variant="outline" onClick={openCompare} disabled={list.length < 1} className="rounded-full">
        <ArrowsLeftRight size={14} weight="duotone" className="mr-1.5" /> Confronta
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="measurements-compare-modal" className="bg-[color:var(--bg-primary)] w-[min(96vw,640px)] max-w-none max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Confronto rilievi</DialogTitle>
          </DialogHeader>
          {L && R && (
            <div className="mt-2">
              <div className="grid grid-cols-2 gap-2 pb-4 border-b border-[color:var(--border)]">
                <DateStepper side="left" idx={idx.left} total={list.length} date={L.date} onStep={(d) => step("left", d)} />
                <DateStepper side="right" idx={idx.right} total={list.length} date={R.date} onStep={(d) => step("right", d)} />
              </div>
              <div className="grid grid-cols-[minmax(0,1.3fr)_1fr_1fr_1.1fr] gap-x-2 text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] py-2">
                <div>Valore</div><div className="text-right">Prec.</div><div className="text-right">Succ.</div><div className="text-right">Differenza</div>
              </div>
              <div className="divide-y divide-[color:var(--border)]" data-testid="mcompare-rows">
                {fields.map((f) => {
                  const a = val(L, f);
                  const b = val(R, f);
                  return (
                    <div key={f.key} data-testid={`mcompare-row-${f.key}`} className="grid grid-cols-[minmax(0,1.3fr)_1fr_1fr_1.1fr] gap-x-2 items-center py-2.5 text-sm">
                      <div className="font-medium">
                        {f.label} <span className="text-[10px] text-[color:var(--text-secondary)]">{f.unit}</span>
                      </div>
                      <div className="text-right tabular-nums" data-testid={`mcompare-left-${f.key}`}>{fmt(a)}</div>
                      <div className="text-right tabular-nums" data-testid={`mcompare-right-${f.key}`}>{fmt(b)}</div>
                      <div className="text-right" data-testid={`mcompare-delta-${f.key}`}><DeltaCell a={a} b={b} field={f} /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
